import { useEffect, useRef } from "react"
import { throttle } from "nodash"
import type { MapMouseEvent } from "maplibre-gl"
import { convertDistance } from "@nova/format"
import { DistanceUnit } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { formatMap } from "plugins/Toolbox/Ruler/layers/RulerMidpointText"
import { getGlobalMapRef } from "../GlobalViewState"
import { TerrainCache } from "../layers/video/terraincache"

const terrainCache = new TerrainCache()
const MOUSEMOVE_THROTTLE_MS = 100

// Elevation-under-cursor for the legend readout. Uses the same terrain-rgb
// tile cache as the video drape feature so this works regardless of whether
// the 3D Terrain toggle (a separate, real MapLibre `terrain` source) is on.
// The caller (ElevationVisual) only mounts while the elevation overlay is
// showing, so there's no separate enabled flag — mount lifecycle is it.
//
// Writes straight to the DOM instead of React state: this fires on every
// throttled mousemove, and routing that through setState would re-render
// the legend subtree at cursor-move frequency for what's just a text node.
export function useElevationReadout() {
    const ref = useRef<HTMLElement>(null)
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const distanceUnitRef = useRef(distanceUnit)
    distanceUnitRef.current = distanceUnit

    useEffect(() => {
        const map = getGlobalMapRef()?.current?.getMap()
        if (!map) return

        const render = (elevation: number | undefined) => {
            const el = ref.current
            if (!el) return
            if (elevation == null) {
                el.textContent = "0 m"
                el.classList.add("invisible")
                return
            }
            const unit = distanceUnitRef.current
            const converted = convertDistance(elevation, DistanceUnit.Meters, unit)
            el.textContent = `${Math.round(converted)} ${formatMap.distance[unit]}`
            el.classList.remove("invisible")
        }

        const onMouseMove = throttle((e: MapMouseEvent) => {
            terrainCache.elevation(e.lngLat.lng, e.lngLat.lat, render)
        }, MOUSEMOVE_THROTTLE_MS)

        const onMouseOut = () => render(undefined)

        map.on("mousemove", onMouseMove)
        map.on("mouseout", onMouseOut)

        return () => {
            map.off("mousemove", onMouseMove)
            map.off("mouseout", onMouseOut)
        }
    }, [])

    return ref
}
